/**
 * 인증 초기화 로직
 */

import type { ITokenManager } from '@edusense/shared-types-auth';
import type { IIdeMessagingService } from '@edusense/shared-types-ide';
import type { AuthState } from '@edusense/shared-types-auth';
import type { User } from '@edusense/shared-types-user';
import { VsCodeAuthResponse } from './types';
import type { ResolvedStorageKeys } from '../storage-keys';
import { resolveStorageKeys } from '../storage-keys';

interface InitializerConfig {
  tokenManager: ITokenManager;
  apiService: any;
  isVSCodeEnv: boolean;
  ideMessengerInstance?: IIdeMessagingService | null;
  setAuthState: (state: AuthState | ((prev: AuthState) => AuthState)) => void;
  fetchUserProfile: (token: string) => Promise<void>;
  storageKeys?: Partial<ResolvedStorageKeys>;
}

/**
 * 인증 초기화 함수 생성
 */
export const createAuthInitializer = (config: InitializerConfig) => {
  return async (): Promise<void> => {
    let initializationCompleted = false;
    
    const completeInitialization = (authData: Partial<AuthState> = {}) => {
      if (!initializationCompleted) {
        initializationCompleted = true;
        config.setAuthState(prev => ({ 
          ...prev, 
          ...authData, 
          isLoading: false, 
          isInitialized: true 
        }));
        console.log('[AuthInitializer] 초기화 완료:', authData);
      }
    };

    console.log('[AuthInitializer] 인증 초기화 시작', {
      isVSCodeEnv: config.isVSCodeEnv,
      hasIdeMessenger: !!config.ideMessengerInstance
    });

    try {
      config.setAuthState(prev => ({ ...prev, isLoading: true, isInitialized: false }));

      if (config.isVSCodeEnv && config.ideMessengerInstance) {
        await initializeVSCodeAuth(config, completeInitialization);
      } else if (!config.isVSCodeEnv) {
        await initializeWebAuth(config, completeInitialization);
      } else {
        console.log('[AuthInitializer] 기타 환경 - 로딩 종료');
        completeInitialization();
      }
    } catch (error) {
      console.error('[AuthInitializer] 초기화 중 오류:', error);
      completeInitialization({ error: '인증 초기화 실패' });
    }

    // 안전장치: 5초 후 강제로 로딩 종료
    setTimeout(() => {
      if (!initializationCompleted) {
        console.warn('[AuthInitializer] 초기화 타임아웃 - 강제로 로딩 종료');
        completeInitialization({ error: '초기화 타임아웃' });
      }
    }, 5000);
  };
};

/**
 * VSCode 환경 초기화
 */
async function initializeVSCodeAuth(
  config: InitializerConfig, 
  completeInitialization: (authData?: Partial<AuthState>) => void
) {
  console.log('[AuthInitializer] VSCode 환경 - 세션 정보 확인');
  
  try {
    const authResponse = await config.ideMessengerInstance!.request<VsCodeAuthResponse | null>(
      'getControlPlaneSessionInfo',
      { silent: true }
    );

    if (authResponse?.accessToken) {
      console.log('[AuthInitializer] VSCode 토큰 발견');
      await config.fetchUserProfile(authResponse.accessToken);
      completeInitialization();
    } else {
      console.log('[AuthInitializer] VSCode 토큰 없음');
      completeInitialization();
    }
  } catch (vscodeError) {
    console.warn('[AuthInitializer] VSCode 세션 정보 가져오기 실패:', vscodeError);
    completeInitialization();
  }
}

/**
 * 웹 환경 초기화
 */
async function initializeWebAuth(
  config: InitializerConfig,
  completeInitialization: (authData?: Partial<AuthState>) => void
) {
  console.log('[AuthInitializer] 웹 환경 초기화 시작');
  const storageKeys = resolveStorageKeys(config.storageKeys);
  
  const storedToken = localStorage.getItem(storageKeys.tokenKey);
  const storedUser = localStorage.getItem(storageKeys.userKey);
  const storedRefreshToken = localStorage.getItem(storageKeys.refreshTokenKey);

  console.log('[AuthInitializer] 저장된 토큰 및 사용자 확인:', {
    hasToken: !!storedToken,
    hasUser: !!storedUser,
    hasRefreshToken: !!storedRefreshToken,
    tokenLength: storedToken?.length || 0
  });

  if (storedToken && storedUser) {
    try {
      const user = JSON.parse(storedUser) as User;
      
      // 토큰 설정
      config.tokenManager.setToken(storedToken);
      config.apiService.setToken(storedToken);

      // 저장된 사용자 정보로 상태 복원 (refreshToken 포함)
      config.setAuthState({
        user,
        token: storedToken,
        refreshToken: storedRefreshToken || null,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        error: null
      });
      
      completeInitialization({ 
        user, 
        token: storedToken, 
        refreshToken: storedRefreshToken || null,
        isAuthenticated: true 
      });
    } catch (error) {
      console.error('[AuthInitializer] 저장된 사용자 정보 파싱 실패:', error);
      localStorage.removeItem(storageKeys.userKey);
      localStorage.removeItem(storageKeys.tokenKey);
      localStorage.removeItem(storageKeys.refreshTokenKey);
      localStorage.removeItem(storageKeys.idTokenKey);
      completeInitialization();
    }
  } else {
    console.log('[AuthInitializer] 웹 환경 - 저장된 토큰 없음');
    completeInitialization();
  }
}
